import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import { DEFAULT_LIMIT_LAUNCHPAD_PROJECTS, useLaunchpadProjectsListInfiniteQuery } from '@query';

import CardProject from '@launchpad/components/common/CardProject/CardProject';
import useIsSelfTestUser from '@launchpad/hooks/useIsSelfTestUser';
import { SvgIconComponent } from '@mui/icons-material';

type PossibleType = 'coming' | 'active' | 'passed';

type PossibleTypeProps = {
  title: string;
  secondaryTitle: string;
  bgClassName: string;
  textClassName: string;
};

type ProjectsProps = {
  type: PossibleType;
  withEarlyAccess?: boolean;
  TitleIcon?: SvgIconComponent;
};

const settingProjects: { [key in PossibleType]: PossibleTypeProps } = {
  coming: {
    title: 'Upcoming Sales',
    secondaryTitle: 'Upcoming Early Access Token Sales',
    bgClassName: 'bg-secondary-700',
    textClassName: 'text-secondary-700',
  },
  active: {
    title: 'Active Sales',
    secondaryTitle: 'Active Early Access Token Sales',
    bgClassName: 'bg-green-700',
    textClassName: 'text-green-700',
  },
  passed: {
    title: 'Completed Project Sales',
    secondaryTitle: 'Completed Early Access Token Sales',
    bgClassName: 'bg-yellow-600',
    textClassName: 'text-yellow-600',
  },
};

const Projects = ({ type, withEarlyAccess, TitleIcon }: ProjectsProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const isTestWallet = useIsSelfTestUser();

  const {
    data: projects,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useLaunchpadProjectsListInfiniteQuery({
    searchParams: {
      limit: DEFAULT_LIMIT_LAUNCHPAD_PROJECTS,
      status: type,
      earlyAccess: withEarlyAccess,
    },
  });

  // TODO: find better solution with skeleton
  useEffect(() => {
    refetch();
  }, [refetch]);

  const filteredProjects = useMemo(() => {
    // TODO: delete this after demo
    if (isTestWallet) {
      return projects?.pages?.flatMap((page) => page) || [];
    }

    return (
      projects?.pages
        ?.flatMap((page) => page)
        ?.filter((project) => project.attributes.slug !== 'demo-sale') || []
    );
  }, [projects, isTestWallet]);

  const projectsList =
    useMemo(() => {
      return filteredProjects.slice(0, visibleCount);
    }, [filteredProjects, visibleCount]) || [];

  const handleLoadMore = async () => {
    await fetchNextPage();
    setCollapsed(false);
    setVisibleCount((prevCount) => prevCount + 3);
  };

  const handleCollapse = () => {
    setCollapsed(true);
    setVisibleCount(3);
  };

  const hasProjects = !!filteredProjects?.length;
  const showCollapseButton =
    !hasNextPage &&
    !isFetchingNextPage &&
    projectsList.length > 3 &&
    !collapsed &&
    visibleCount >= filteredProjects.length;

  const showSeeMoreButton =
    (hasNextPage || collapsed || visibleCount < filteredProjects.length) &&
    hasProjects &&
    !isLoading;

  const title = withEarlyAccess
    ? settingProjects[type].secondaryTitle
    : settingProjects[type].title;

  const TitleComponent = type === 'passed' ? 'h1' : 'h2';

  if (!isLoading && !hasProjects) {
    return null;
  }

  return (
    <section>
      <div>
        <TitleComponent className="flex justify-start items-center gap-2 pb-12 text-[32px] font-semibold">
          {!TitleIcon && (
            <span
              className={classNames(
                'block w-4 h-4 rounded-full',
                settingProjects[type].bgClassName,
              )}
            ></span>
          )}
          {TitleIcon && (
            <TitleIcon className={classNames('icon-sm', settingProjects[type].textClassName)} />
          )}
          {title}
        </TitleComponent>

        {isLoading && (
          <div>
            <div className="mb-12 skeleton-block w-full h-[500px]" />
            <div className="flex justify-center w-full mb-12">
              <div className="ui-button ui-button-solid-secondary h-12 w-[165px] skeleton-block" />
            </div>
          </div>
        )}

        <div className="flex flex-row flex-wrap gap-4 justify-start">
          {!isLoading &&
            hasProjects &&
            projectsList.map((project) => (
              <CardProject
                key={project.id}
                status={type}
                project={project}
                withEarlyAccess={withEarlyAccess}
                className="lg:max-w-[calc(33.333333%_-_16px)] md:max-w-[calc(50%_-_16px)] mb-12"
              />
            ))}
        </div>
        {showSeeMoreButton && (
          <div className="flex justify-center w-full mb-12">
            <button
              className="ui-button ui-button-solid-secondary h-12 text-2xl flex gap-2"
              onClick={handleLoadMore}
            >
              Show More
              {isFetchingNextPage && (
                <Spinner
                  key={`loading_button_${type}`}
                  className="self-center justify-self-center m-auto"
                  size="sm"
                />
              )}
            </button>
          </div>
        )}
        {showCollapseButton && (
          <div className="flex justify-center w-full mb-12">
            <button
              className="ui-button ui-button-solid-secondary h-12 text-2xl"
              onClick={handleCollapse}
            >
              Collapse
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
